import { getActiveWorldConfig, hasActiveWorldConfig, type StartingAvatarLineage, type StartingAvatarsConfig } from '@gamecodex/world-config'

/** The old generated placeholder (DiceBear "bottts" robot). */
export function generatedAvatarUrl(seed: string): string {
  return `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(seed)}`
}

/**
 * This world's in-world starting headshots, if it configures any, narrowed to the ones it currently OFFERS
 * (`chargen.looks` hides groups or lineages without deleting their data). Pickers and the default read this, so a
 * hidden lineage is never offered to a new pick; a portrait a player already chose is stored by URL and keeps working.
 */
export function startingAvatarsConfig(): StartingAvatarsConfig | null {
  if (!hasActiveWorldConfig()) return null
  const world = getActiveWorldConfig()
  const cfg = world.startingAvatars
  if (!cfg) return null
  const hideGroups = new Set(world.chargen?.looks?.hideGroups ?? [])
  const hideLineages = new Set(world.chargen?.looks?.hideLineages ?? [])
  const offered =
    hideGroups.size || hideLineages.size
      ? (() => {
          const lineages = cfg.lineages.filter((l) => !hideLineages.has(l.id) && !(l.group && hideGroups.has(l.group)))
          const ids = new Set(lineages.map((l) => l.id))
          return { ...cfg, lineages, portraits: cfg.portraits.filter((p) => ids.has(p.lineageId)) }
        })()
      : cfg
  return offered.portraits.length > 0 ? offered : null
}

/**
 * True when an avatar URL is a placeholder rather than a player's own choice:
 * empty, the generated robot, or a stale local `blob:` preview.
 */
export function isPlaceholderAvatar(url: string | null | undefined): boolean {
  if (!url) return true
  return url.startsWith('https://api.dicebear.com/') || url.startsWith('blob:')
}

function hash(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/**
 * Default avatar for a new player. Worlds with starting headshots get a stable
 * in-world portrait picked from the seed; other worlds keep the generated one.
 */
export function defaultAvatarFor(seed: string): string {
  const cfg = startingAvatarsConfig()
  if (!cfg) return generatedAvatarUrl(seed)
  return cfg.portraits[hash(seed) % cfg.portraits.length].url
}

/**
 * The avatar to show: the player's own choice when they made one, otherwise
 * (on worlds with starting headshots) an in-world portrait in place of the
 * placeholder. Other worlds get `url` back unchanged.
 */
export function effectiveAvatarUrl(url: string | null | undefined, seed: string): string | null {
  if (!isPlaceholderAvatar(url)) return url ?? null
  return startingAvatarsConfig() ? defaultAvatarFor(seed) : url ?? null
}

/** {@link effectiveAvatarUrl} for a signed-in profile (seeded like sign-in). */
export function userAvatarUrl(user: { avatarUrl: string | null; displayName: string; email?: string } | null | undefined): string | null {
  if (!user) return null
  return effectiveAvatarUrl(user.avatarUrl, user.displayName || user.email || '')
}

/**
 * Lineages bucketed by their optional `group`, in first-seen order. Ungrouped
 * lineages form one unlabeled bucket, so worlds without groups keep a flat row.
 */
export function lineageGroups(lineages: StartingAvatarLineage[]): { group: string | null; lineages: StartingAvatarLineage[] }[] {
  const out: { group: string | null; lineages: StartingAvatarLineage[] }[] = []
  for (const l of lineages) {
    const key = l.group ?? null
    let bucket = out.find((b) => b.group === key)
    if (!bucket) {
      bucket = { group: key, lineages: [] }
      out.push(bucket)
    }
    bucket.lineages.push(l)
  }
  return out
}
